import { inject } from '@angular/core';
import { TelemetryPeriod, samePeriod } from '@core/constants/telemetry-route.constant';
import { DeviceEnergyflow } from '@core/models';
import { InsightsService } from '@core/services/insights.service';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, from, map, mergeMap, of, pipe, tap } from 'rxjs';

type DeviceEnergyflowState = {
  responses: Record<string, DeviceEnergyflow>;
  pending: string[];
};

export interface EnergyflowRequest {
  deviceIds: string[];
  period: TelemetryPeriod;
}

function energyflowKey(deviceId: string, period: TelemetryPeriod): string {
  return `${deviceId}|${period.granularity}|${period.period}`;
}

// Provided by the installation detail page beside its telemetry store, so the insights and data
// tabs share one read per device and period. A failed read is not kept: the card goes without the
// series it feeds, and the next request for that period asks again. `getDevicePvForecast` types
// only the PV part, but it hands back the whole energyflow response, heat pump entries included.
export const DeviceEnergyflowStore = signalStore(
  withState<DeviceEnergyflowState>({ responses: {}, pending: [] }),
  withMethods((store, insightsService = inject(InsightsService)) => ({
    // The telemetry store keeps the previous period's response on screen while the selected one
    // loads, so a read is only handed out while the response on screen is for the period it was
    // read for. Lined up with another period's slots, it would draw in the wrong place.
    responseFor: (
      deviceId: string,
      selected: TelemetryPeriod,
      shown: TelemetryPeriod | null,
    ): DeviceEnergyflow | null =>
      samePeriod(selected, shown)
        ? (store.responses()[energyflowKey(deviceId, selected)] ?? null)
        : null,

    load: rxMethod<EnergyflowRequest>(
      pipe(
        mergeMap(({ deviceIds, period }) => {
          const wanted = deviceIds.filter(deviceId => {
            const key = energyflowKey(deviceId, period);
            return !(key in store.responses()) && !store.pending().includes(key);
          });
          if (wanted.length > 0) {
            patchState(store, {
              pending: [...store.pending(), ...wanted.map(id => energyflowKey(id, period))],
            });
          }
          return from(wanted).pipe(
            mergeMap(deviceId =>
              insightsService.getDevicePvForecast(deviceId, period).pipe(
                map(response => ({ key: energyflowKey(deviceId, period), response })),
                catchError(() => of({ key: energyflowKey(deviceId, period), response: null })),
              ),
            ),
          );
        }),
        tap(({ key, response }) =>
          patchState(store, state => ({
            pending: state.pending.filter(pendingKey => pendingKey !== key),
            responses: response ? { ...state.responses, [key]: response } : state.responses,
          })),
        ),
      ),
    ),
  })),
);
